import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag, Typography, Button, Avatar, Image, Tooltip, Empty, message } from 'antd';
import { ArrowLeftOutlined, UserOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs from '_helpers/datetime';
import { useAttendanceActions } from '_actions/attendance.actions';
import { useSessionWeek } from '_helpers/useSessionWeek';
import { homeworkLabel } from '_helpers/homework';

const { Text } = Typography;

// Mirror of the attendance tab's status palette (read-only here).
const STATUS_LABEL = { '': 'Chưa ĐD', present: 'Có mặt', absent: 'Vắng', late: 'Muộn', excused: 'Có phép', paused: 'Bảo lưu' };
const STATUS_COLOR = { '': '#BFBFBF', present: '#4CAF6E', absent: '#D95C5C', late: '#E8A04C', excused: '#5B8DEF', paused: '#9AA0A6' };
const HW_COLOR = { excellent: '#4CAF6E', complete: '#52B6CE', incomplete: '#E8A04C', none: '#D95C5C' };

export { ClassOverviewAttendance };

// Read-only, full teacher-completion view of one class's attendance:
// status + homework + attitude + rating + the proof photo per session. No edit controls.
function ClassOverviewAttendance({ classId, className, onBack }) {
    const { getManagerAttendance, confirmPunctuality, setSessionTaught } = useAttendanceActions();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Manager confirms / un-confirms the teacher was on time (đúng giờ) → KPI punctuality dimension.
    async function onConfirmPunctuality(sessionId, confirm) {
        const res = await confirmPunctuality(classId, sessionId, confirm);
        if (res?.status === 'Success') {
            setSessions(prev => prev.map(s => s._id === sessionId ? { ...s, punctuality_confirmed: confirm } : s));
            message.success(confirm ? 'Đã xác nhận đúng giờ' : 'Đã bỏ xác nhận');
        } else {
            message.error('Thao tác thất bại');
        }
    }

    // Manager records/undoes "đã dạy" → the session counts toward Tổng giờ dạy. Only touches teach
    // time; punctuality for a normal class still keys off the proof photo, not this.
    async function onSetTaught(sessionId, taught) {
        const res = await setSessionTaught(classId, sessionId, taught);
        if (res?.status === 'Success') {
            setSessions(prev => prev.map(s => s._id === sessionId
                ? { ...s, teacher_check_in_at: res.message.teacher_check_in_at, check_in_manual: res.message.check_in_manual }
                : s));
            message.success(taught ? 'Đã ghi nhận giờ dạy' : 'Đã bỏ ghi nhận');
        } else {
            message.error(typeof res?.message === 'string' ? res.message : 'Thao tác thất bại');
        }
    }

    useEffect(() => {
        (async () => {
            setLoading(true);
            setSessions(await getManagerAttendance(classId));
            setLoading(false);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId]);

    // Week window — same nav as the attendance grid. Roster stays the union of ALL sessions.
    const { weekSessions, controls } = useSessionWeek(sessions, classId);

    // Roster = union of students across all sessions; per-session lookup by student id.
    const { students, recordMaps } = useMemo(() => {
        const byId = new Map();
        const maps = {};
        for (const s of sessions) {
            const m = new Map();
            for (const r of (s.records || [])) {
                const stu = r.student_ref;
                if (!stu || !stu._id) continue;
                if (!byId.has(stu._id)) byId.set(stu._id, stu);
                m.set(stu._id, r);
            }
            maps[s._id] = m;
        }
        const list = [...byId.values()].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'vi'));
        return { students: list, recordMaps: maps };
    }, [sessions]);

    const columns = useMemo(() => {
        const cols = [{
            title: 'Học sinh',
            key: 'student',
            fixed: 'left',
            width: 200,
            render: (_, stu) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {stu.avatar
                        ? <Avatar src={`/api/photos/${stu.avatar}`} size={32} />
                        : <Avatar size={32} icon={<UserOutlined />} />}
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{stu.name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{stu.student_id}</div>
                    </div>
                </div>
            ),
        }];

        for (const s of weekSessions) {
            const d = s.date ? dayjs(s.date) : null;
            cols.push({
                key: s._id,
                width: 160,
                title: (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>{s.lesson_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                            {d ? d.format('DD/MM/YYYY') : ''}{d ? ` · ${d.format('HH:mm')}` : ''}
                        </div>
                        {(s.held || s.proof) && <Tag color="green" style={{ marginTop: 2 }}>Đã học</Tag>}
                        {s.cancelled &&
                            <Tooltip title={s.cancel_reason || 'Đã hủy'}>
                                <Tag color="warning" style={{ marginTop: 2 }}>Đã hủy</Tag>
                            </Tooltip>}
                        {/* Teacher punctuality (đúng giờ) */}
                        {!s.cancelled && (
                            <div style={{ marginTop: 4 }}>
                                {s.teacher_check_in_at ? (
                                    <div>
                                        <Tag color={s.check_in_late ? 'error' : 'green'} style={{ margin: 0 }}>
                                            GV {dayjs(s.teacher_check_in_at).format('HH:mm')}{s.check_in_late ? ' (muộn)' : ''}
                                        </Tag>
                                        {s.check_in_manual &&
                                            <div><Text type="secondary" style={{ fontSize: 11 }}>QL ghi nhận</Text></div>}
                                    </div>
                                ) : (
                                    <Text type="secondary" style={{ fontSize: 11 }}>GV chưa điểm danh</Text>
                                )}
                                {/* Back-fill teach time. The teacher's own check-in window closes 15 min
                                    after the lesson ends, so without this a lesson they really taught is
                                    unrecordable — and every session predating check-in has none at all. */}
                                {s.check_in_manual ? (
                                    <Button size="small" style={{ marginTop: 2 }}
                                        onClick={() => onSetTaught(s._id, false)}>Bỏ “đã dạy”</Button>
                                ) : !s.teacher_check_in_at && (
                                    <Tooltip title="Ghi nhận giáo viên đã dạy buổi này (tính vào tổng giờ dạy)">
                                        <Button size="small" style={{ marginTop: 2 }}
                                            onClick={() => onSetTaught(s._id, true)}>Đã dạy</Button>
                                    </Tooltip>
                                )}
                                {s.punctuality_confirmed ? (
                                    <Button size="small" type="primary" ghost icon={<CheckOutlined />}
                                        style={{ marginTop: 2 }}
                                        onClick={() => onConfirmPunctuality(s._id, false)}>Đã xác nhận</Button>
                                ) : (
                                    <Tooltip title={s.teacher_check_in_at ? 'Xác nhận giáo viên đúng giờ' : 'Giáo viên chưa điểm danh — vẫn có thể xác nhận thủ công'}>
                                        <Button size="small" icon={<CheckOutlined />} style={{ marginTop: 2 }}
                                            onClick={() => onConfirmPunctuality(s._id, true)}>Đúng</Button>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        <div style={{ marginTop: 4 }}>
                            {s.proof
                                ? <Image src={`/api/photos/${s.proof}`} width={48} height={48}
                                    style={{ objectFit: 'cover', borderRadius: 4 }} />
                                : <Text type="secondary" style={{ fontSize: 11 }}>chưa có ảnh</Text>}
                        </div>
                    </div>
                ),
                align: 'center',
                render: (_, stu) => {
                    const r = recordMaps[s._id]?.get(stu._id);
                    if (!r) return <Text type="secondary">—</Text>;
                    return (
                        <Tooltip title={[
                            r.attitude && `Ý thức: ${r.attitude}`,
                            r.comment && `Ghi chú: ${r.comment}`,
                        ].filter(Boolean).join('\n') || null}>
                            <div style={{ lineHeight: 1.6 }}>
                                <Tag color={STATUS_COLOR[r.status || '']} style={{ margin: 0 }}>
                                    {STATUS_LABEL[r.status || '']}
                                </Tag>
                                {r.homework &&
                                    <div style={{ fontSize: 11, color: HW_COLOR[r.homework] }}>
                                        BTVN: {homeworkLabel(r.homework)}
                                    </div>}
                                {r.rating > 0 &&
                                    <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Điểm: {r.rating}/10</div>}
                            </div>
                        </Tooltip>
                    );
                },
            });
        }
        return cols;
    }, [weekSessions, recordMaps]);

    return (
        <div className="p-4">
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Quản lý lớp học</Button>
                <Text strong style={{ fontSize: 16 }}>{className}</Text>
                <Text type="secondary">(chỉ xem)</Text>
            </div>
            <div style={{ marginBottom: 12 }}>{controls}</div>
            <Table
                columns={columns}
                dataSource={students.map(s => ({ ...s, key: s._id }))}
                loading={loading}
                size="small"
                bordered
                pagination={false}
                scroll={{ x: 'max-content', y: '70vh' }}
                locale={{ emptyText: <Empty description="Chưa có buổi học nào" /> }}
            />
        </div>
    );
}
